import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  Schedule,
  FlightTakeoff,
  TrendingUp,
  TrendingDown,
  Assignment,
  TrackChanges
} from '@mui/icons-material';

const MetricCard = ({ title, value, icon: Icon, color, subtitle, progress, trend }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ pb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: color }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ 
          bgcolor: `${color}.light`, 
          borderRadius: '50%', 
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon sx={{ color: color, fontSize: 24 }} />
        </Box>
      </Box>
      
      {progress !== undefined && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {progress}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
                borderRadius: 3,
              }
            }} 
          />
        </Box>
      )}
      
      {trend && (
        <Box sx={{ 
          position: 'absolute', 
          top: 8, 
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
          {trend > 0 ? (
            <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
          ) : (
            <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />
          )}
          <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
            {Math.abs(trend)}%
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const MetricsOverview = ({ data }) => {
  if (!data) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <Card sx={{ height: 140 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  const { summary, fedex_metrics, period_info } = data;

  const metrics = [
    {
      title: 'Total Shipments',
      value: summary.total_shipments,
      icon: LocalShipping,
      color: 'primary.main',
      subtitle: `${period_info?.period?.toUpperCase()} period`,
    },
    {
      title: 'Delivered',
      value: summary.delivered_shipments,
      icon: CheckCircle,
      color: 'success.main',
      progress: summary.delivery_rate,
      subtitle: `${summary.delivery_rate}% delivery rate`,
    },
    {
      title: 'Pending',
      value: summary.pending_shipments,
      icon: Schedule,
      color: 'warning.main',
      subtitle: 'Awaiting delivery',
    },
    {
      title: 'Master AWBs',
      value: summary.total_master_awbs,
      icon: Assignment,
      color: 'info.main',
      subtitle: 'Total AWB records',
    },
    {
      title: 'Child AWBs',
      value: summary.total_child_awbs,
      icon: TrackChanges,
      color: 'secondary.main',
      subtitle: `${fedex_metrics?.tracking_rate || 0}% tracked`,
    },
    {
      title: 'Avg Delivery',
      value: summary.avg_delivery_days ? `${summary.avg_delivery_days} days` : 'N/A',
      icon: FlightTakeoff,
      color: 'info.main',
      subtitle: 'Average time',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Performance Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip 
            label={`${fedex_metrics?.fedex_delivery_rate || 0}% FedEx Delivery Rate`}
            color="primary"
            size="small"
            variant="outlined"
          />
          <Chip 
            label={`${summary.delivery_rate}% Overall Rate`}
            color={summary.delivery_rate >= 80 ? 'success' : 'warning'}
            size="small"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {/* Additional FedEx Metrics */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          FedEx Tracking Performance
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {fedex_metrics?.tracked_awbs || 0}
                </Typography>
                <Typography variant="body2">
                  FedEx Tracked AWBs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {fedex_metrics?.delivered_awbs || 0}
                </Typography>
                <Typography variant="body2">
                  FedEx Delivered
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {fedex_metrics?.tracking_rate || 0}%
                </Typography>
                <Typography variant="body2">
                  Tracking Coverage
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MetricsOverview;